import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "./common/Layout";
import "../css/accountManagement.scss";
import { setAPIAuthorizationToken } from "../apis/API";
import Cookies from "js-cookie";

const AccountManagement = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [formData, setFormData] = useState({
    accountName: "",
    accountNumber: "",
    currency: "USD",
    balance: "",
    status: "active",
    owner: "",
  });

  // 初始化token
  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      setAPIAuthorizationToken(token);
    }
  }, []);

  // 模拟账户数据
  useEffect(() => {
    const mockAccounts = [
      {
        id: 1,
        accountName: "美元主账户",
        accountNumber: "US1234567890",
        currency: "USD",
        balance: "125,680.50",
        status: "active",
        owner: "张三",
        createTime: "2025-01-10",
      },
      {
        id: 2,
        accountName: "欧元账户",
        accountNumber: "EU0987654321",
        currency: "EUR",
        balance: "86,420.30",
        status: "active",
        owner: "李四",
        createTime: "2025-02-15",
      },
      {
        id: 3,
        accountName: "英镑账户",
        accountNumber: "GB1122334455",
        currency: "GBP",
        balance: "54,200.00",
        status: "active",
        owner: "王五",
        createTime: "2025-03-20",
      },
      {
        id: 4,
        accountName: "人民币账户",
        accountNumber: "CN9988776655",
        currency: "CNY",
        balance: "890,350.80",
        status: "frozen",
        owner: "赵六",
        createTime: "2025-04-01",
      },
      {
        id: 5,
        accountName: "日元账户",
        accountNumber: "JP5544332211",
        currency: "JPY",
        balance: "15,680,500",
        status: "active",
        owner: "钱七",
        createTime: "2025-05-08",
      },
    ];
    setAccounts(mockAccounts);
  }, []);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredAccounts = accounts.filter(
    (account) =>
      account.accountName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.accountNumber.includes(searchTerm) ||
      account.owner.includes(searchTerm)
  );

  const handleEditAccount = (account) => {
    setCurrentAccount(account);
    setFormData({
      accountName: account.accountName,
      accountNumber: account.accountNumber,
      currency: account.currency,
      balance: account.balance.replace(/,/g, ""),
      status: account.status,
      owner: account.owner,
    });
    setShowEditModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (currentAccount) {
      setAccounts(
        accounts.map((account) =>
          account.id === currentAccount.id
            ? {
                ...account,
                ...formData,
                balance: parseFloat(formData.balance).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }),
              }
            : account
        )
      );
      setShowEditModal(false);
    }
  };

  const getStatusName = (status) => {
    const statusMap = {
      active: "正常",
      frozen: "冻结",
      closed: "已关闭",
    };
    return statusMap[status] || status;
  };

  const getCurrencySymbol = (currency) => {
    const symbols = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      CNY: "¥",
      JPY: "¥",
    };
    return symbols[currency] || currency;
  };

  return (
    <Layout>
      <div className="account-management">
        <div className="page-header">
          <h1>账户管理</h1>
          <div className="header-actions">
            <button
              className="btn-primary"
              onClick={() => navigate("/apply-account")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 5v14M5 12h14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              申请新账户
            </button>
          </div>
        </div>

        <div className="search-bar">
          <div className="search-input-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="8" stroke="#999" strokeWidth="2" />
              <path
                d="M21 21l-4.35-4.35"
                stroke="#999"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <input
              type="text"
              placeholder="搜索账户名称、账号或持有人..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
        </div>

        <div className="accounts-grid">
          {filteredAccounts.map((account) => (
            <div key={account.id} className="account-card">
              <div className="account-card-header">
                <div className="account-title">
                  <h3>{account.accountName}</h3>
                  <span className={`status-badge status-${account.status}`}>
                    {getStatusName(account.status)}
                  </span>
                </div>
                <button
                  className="btn-edit-icon"
                  onClick={() => handleEditAccount(account)}
                  title="编辑账户"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
              <div className="account-card-body">
                <div className="account-info-row">
                  <span className="label">账号:</span>
                  <span className="value">{account.accountNumber}</span>
                </div>
                <div className="account-info-row">
                  <span className="label">持有人:</span>
                  <span className="value">{account.owner}</span>
                </div>
                <div className="account-balance">
                  <span className="balance-label">余额</span>
                  <div className="balance-amount">
                    <span className="currency">
                      {getCurrencySymbol(account.currency)}
                    </span>
                    <span className="amount">{account.balance}</span>
                    <span className="currency-code">{account.currency}</span>
                  </div>
                </div>
                <div className="account-footer">
                  <span className="create-time">
                    创建于 {account.createTime}
                  </span>
                  <button
                    className="btn-view"
                    onClick={() => navigate(`/store-detail/${account.id}`)}
                  >
                    查看详情 →
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredAccounts.length === 0 && (
          <div className="empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#ccc" strokeWidth="2" />
              <path
                d="M12 8v4M12 16h.01"
                stroke="#ccc"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <p>没有找到符合条件的账户</p>
          </div>
        )}
      </div>

      {/* 编辑账户模态框 */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>编辑账户信息</h2>
              <button
                className="close-btn"
                onClick={() => setShowEditModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>账户名称 *</label>
                <input
                  type="text"
                  value={formData.accountName}
                  onChange={(e) =>
                    setFormData({ ...formData, accountName: e.target.value })
                  }
                  required
                  placeholder="请输入账户名称"
                />
              </div>
              <div className="form-group">
                <label>账号 *</label>
                <input
                  type="text"
                  value={formData.accountNumber}
                  onChange={(e) =>
                    setFormData({ ...formData, accountNumber: e.target.value })
                  }
                  required
                  placeholder="请输入账号"
                  disabled
                  style={{ background: "#f5f5f5", cursor: "not-allowed" }}
                />
              </div>
              <div className="form-group">
                <label>币种 *</label>
                <select
                  value={formData.currency}
                  onChange={(e) =>
                    setFormData({ ...formData, currency: e.target.value })
                  }
                  required
                >
                  <option value="USD">USD - 美元</option>
                  <option value="EUR">EUR - 欧元</option>
                  <option value="GBP">GBP - 英镑</option>
                  <option value="CNY">CNY - 人民币</option>
                  <option value="JPY">JPY - 日元</option>
                </select>
              </div>
              <div className="form-group">
                <label>余额 *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.balance}
                  onChange={(e) =>
                    setFormData({ ...formData, balance: e.target.value })
                  }
                  required
                  placeholder="请输入余额"
                />
              </div>
              <div className="form-group">
                <label>持有人 *</label>
                <input
                  type="text"
                  value={formData.owner}
                  onChange={(e) =>
                    setFormData({ ...formData, owner: e.target.value })
                  }
                  required
                  placeholder="请输入持有人姓名"
                />
              </div>
              <div className="form-group">
                <label>状态 *</label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                  required
                >
                  <option value="active">正常</option>
                  <option value="frozen">冻结</option>
                  <option value="closed">已关闭</option>
                </select>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  取消
                </button>
                <button type="submit" className="btn-primary">
                  保存修改
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <footer className="page-footer">
        <p>© 2022 GLOBAL E-PAYMENT LIMITED. All Rights Reserved.</p>
      </footer>
    </Layout>
  );
};

export default AccountManagement;
